// 09 §1.1: "שיתוף" — drawer זמני בצד ימין (אותה מעטפת של "חיבור Gmail"/קונסנט), רק ליוצר היצירה. יצירה פרטית ליוצר; פרסום ≠ שיתוף.
// כל המופע (מסונן לפי min_role) ו/או משתמשים מסוימים; שיתוף = צפייה + הרצה בלבד. טיוטה מקומית → "שמירה" (PUT, המצב המלא)
// עם משפט אישור מפורש של מה ייקרה. נסגר ב-X / Escape / לחיצה בחוץ.
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import type { ArtifactShares } from '../lib/contract.ts';
import { T } from '../lib/strings.ts';
import { ErrorView, Loading } from './Status.tsx';
import { Badge } from './ui/badge.tsx';
import { Button } from './ui/button.tsx';
import { Select } from './ui/input.tsx';
import { Sheet } from './ui/sheet.tsx';
import { IconAlert, IconCheck, IconLock, IconUsers, IconX } from './brand/icons.tsx';

/** צ'יפ השיתוף בכרטיס ובכותרת: "פרטי" / "משותף" (ליוצר), "משותף איתי · <שם>" (לאחר). */
export function ShareChip({ shared, sharedBy }: { shared?: ArtifactShares | undefined; sharedBy?: { nameHe: string } | null | undefined }) {
  if (shared) {
    const on = shared.everyone || shared.users.length > 0;
    return (
      <Badge tone={on ? 'info' : 'neutral'} className="gap-1" data-testid="share-chip" data-share={on ? 'shared' : 'private'}>
        <IconUsers size={14} />
        {on ? T.share.shared : T.share.private}
      </Badge>
    );
  }
  if (sharedBy)
    return (
      <Badge tone="info" className="gap-1" data-testid="share-chip" data-share="with-me">
        <IconUsers size={14} />
        {T.share.sharedWithMe(sharedBy.nameHe)}
      </Badge>
    );
  return null;
}

export function ShareDrawer({ open, onClose, slug, nameHe, minRole, published }: { open: boolean; onClose: () => void; slug: string; nameHe: string; minRole: string; published: boolean }) {
  return (
    <Sheet open={open} onClose={onClose} title={`${T.share.title} · ${nameHe}`} side="start" className="max-w-md" testId="share-drawer">
      <div className="flex items-center gap-2 border-b border-border p-3 ps-4">
        <IconUsers size={20} className="text-primary" />
        <h2 className="m-0 me-auto min-w-0 truncate text-lg font-bold">
          {T.share.title} · {nameHe}
        </h2>
        <Button variant="ghost" size="icon" aria-label={T.share.close} title={T.share.close} onClick={onClose} data-testid="share-close">
          <IconX size={20} />
        </Button>
      </div>
      {open && <Body slug={slug} nameHe={nameHe} minRole={minRole} published={published} onClose={onClose} />}
    </Sheet>
  );
}

function Body({ slug, nameHe, minRole, published, onClose }: { slug: string; nameHe: string; minRole: string; published: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['artifact-share', slug], queryFn: () => api.getShare(slug), staleTime: 0 });
  const [everyone, setEveryone] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  useEffect(() => {
    if (!q.data) return;
    setEveryone(q.data.shared.everyone);
    setIds(q.data.shared.users.map((u) => u.id));
  }, [q.data]);

  const people = useMemo(() => {
    const m = new Map<string, { id: string; nameHe: string; email?: string | undefined; sharedBy?: { nameHe: string } | undefined }>();
    for (const u of q.data?.shared.users ?? []) m.set(u.id, u);
    for (const c of q.data?.candidates ?? []) m.set(c.id, { ...c, ...(m.get(c.id)?.sharedBy ? { sharedBy: m.get(c.id)!.sharedBy } : {}) });
    return m;
  }, [q.data]);
  const options = [...people.values()].filter((p) => !ids.includes(p.id)).sort((a, b) => a.nameHe.localeCompare(b.nameHe, 'he'));
  const chosen: Array<{ id: string; nameHe: string; email?: string | undefined; sharedBy?: { nameHe: string } | undefined }> = ids.map((id) => people.get(id) ?? { id, nameHe: id });

  if (q.isPending) return <Loading />;
  if (q.isError) return <div className="p-4"><ErrorView error={q.error} onRetry={() => q.refetch()} /></div>;

  // data-access-rls §7.1: לכל משתמש מוגבל, ההערה של השרת ("רואה רק את הנתונים שהוקצו לו" / "הדוח ייכשל אצלו: …")
  const notes = q.data.dataAccess ?? {};
  const restrictedAll = everyone ? Object.keys(notes).filter((id) => !ids.includes(id) && people.has(id)) : [];
  const who = [...(everyone ? [T.share.everyone] : []), ...chosen.map((c) => c.nameHe)].join(', ');
  const save = async () => {
    setErr(null);
    setBusy(true);
    try {
      await api.putShare(slug, { everyone, userIds: ids });
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['artifact-share', slug] });
      void qc.invalidateQueries({ queryKey: ['artifacts'] });
      void qc.invalidateQueries({ queryKey: ['artifact', slug] });
      setTimeout(onClose, 900);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="share-body">
      <p className="m-0 text-[15px] text-muted-foreground">{T.share.explain}</p>
      {!published && (
        <div className="flex items-start gap-2 rounded-md border border-degraded/30 bg-degraded/10 text-degraded p-3 text-sm" data-testid="share-not-published">
          <IconAlert size={18} className="mt-0.5 shrink-0" />
          {T.share.notPublished}
        </div>
      )}

      <label className="flex min-h-11 cursor-pointer items-start gap-3 rounded-md border border-border bg-card p-3 shadow-card">
        <input
          type="checkbox"
          className="mt-1 size-5 shrink-0 accent-[hsl(var(--primary))]"
          checked={everyone}
          onChange={(e) => {
            setEveryone(e.target.checked);
            setSaved(false);
          }}
          data-testid="share-everyone"
        />
        <span className="flex flex-col">
          <span className="font-semibold">{T.share.everyone}</span>
          <span className="text-sm text-muted-foreground">{T.share.everyoneHint(T.roles[minRole] ?? minRole)}</span>
          {restrictedAll.map((id) => (
            <span key={id} className="flex flex-wrap items-center gap-x-1 text-sm" data-user={id}>
              {people.get(id)!.nameHe}: <DaNote text={notes[id]!.noteHe} />
            </span>
          ))}
        </span>
      </label>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-semibold text-foreground/90">{T.share.people}</span>
        <div className="flex gap-2">
          <Select aria-label={T.share.pick} value={pick} onChange={(e) => setPick(e.target.value)} data-testid="share-pick">
            <option value="">{T.share.pickNone}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.nameHe}
                {o.email ? ` · ${o.email}` : ''}
              </option>
            ))}
          </Select>
          <Button
            variant="outline"
            disabled={!pick}
            onClick={() => {
              setIds((x) => [...x, pick]);
              setPick('');
              setSaved(false);
            }}
            data-testid="share-add"
          >
            {T.share.add}
          </Button>
        </div>
        {chosen.length ? (
          <ul className="m-0 flex list-none flex-col divide-y divide-border overflow-hidden rounded-md border border-border bg-card p-0" data-testid="share-users">
            {chosen.map((c) => (
              <li key={c.id} className="flex min-h-11 items-center gap-2 px-3 py-1.5" data-user={c.id}>
                <span className="me-auto flex min-w-0 flex-col">
                  <span className="truncate">
                    {c.nameHe}
                    {c.email && <span className="text-sm text-muted-foreground"> · {c.email}</span>}
                    {/* users-delegation: שיתוף הלאה של בעל האצלה; היוצר רואה מי שיתף ויכול להסיר */}
                    {c.sharedBy && <span className="text-sm text-muted-foreground" data-testid="share-by"> · {T.share.sharedByOther(c.sharedBy.nameHe)}</span>}
                  </span>
                  {notes[c.id] && <DaNote text={notes[c.id]!.noteHe} />}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={T.share.remove(c.nameHe)}
                  title={T.share.remove(c.nameHe)}
                  onClick={() => {
                    setIds((x) => x.filter((id) => id !== c.id));
                    setSaved(false);
                  }}
                  data-testid="share-remove"
                >
                  <IconX size={18} />
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          !everyone && <p className="m-0 text-sm text-muted-foreground">{T.share.none}</p>
        )}
      </div>

      <p className="m-0 rounded-md bg-muted p-3 text-sm" role="status" data-testid="share-confirm">
        {who ? T.share.confirmShare(who, nameHe) : T.share.confirmPrivate(nameHe)}
      </p>
      {err != null && <ErrorView error={err} />}
      <div className="flex items-center gap-3">
        <Button onClick={() => void save()} disabled={busy} data-testid="share-save">
          {busy ? T.share.saving : T.share.save}
        </Button>
        {saved && (
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-ok" data-testid="share-saved">
            <IconCheck size={16} />
            {T.share.saved}
          </span>
        )}
      </div>
    </div>
  );
}

/** ההערה של השרת למשתמש מוגבל, כדי שהבעלים יבין למה הדוח המשותף מציג אצלו פחות (או ייכשל). */
function DaNote({ text }: { text: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-[13px] text-muted-foreground" data-testid="share-da-note">
      <IconLock size={12} className="shrink-0" />
      {text}
    </span>
  );
}
